import { useRef, useState } from 'react'
import { FIELD_SPECS, mayEditField, renderValue } from '../command-center/pursuit/fieldEdit.js'
import { factProblem } from './durableFacts.js'
import styles from './championToClose.module.css'

// Keyed by exact record + fact revision by the parent. Inputs never migrate
// across records, and review is invalidated by any intervening fact change.
export default function DurableFactEditor({ facts, field, onResult }) {
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState(facts.raw?.[field] || '')
  const [reason, setReason] = useState('')
  const [declared, setDeclared] = useState(false)
  const [evidenceReviewed, setEvidenceReviewed] = useState(false)
  const [stage, setStage] = useState('edit')
  const [result, setResult] = useState(null)
  const pending = useRef(false)
  const gate = mayEditField(facts.policy, field)
  const spec = FIELD_SPECS[field]
  if (!facts.ready || !gate.editable) return <span className={styles.gapSm}>{gate.why || 'Current field policy unavailable; editing is disabled.'}</span>
  const raw = facts.raw
  const problem = factProblem({ raw, field, value, reason, policy: facts.policy, declared, evidenceReviewed })
  const evidence = field === 'champion_story_source' ? value.trim() : raw.champion_story_source
  const resetReview = () => { setStage('edit'); setResult(null); setEvidenceReviewed(false) }
  const save = async () => {
    if (pending.current || problem || stage !== 'review') return
    pending.current = true; setStage('working')
    const outcome = await facts.save({ raw, field, value, reason, declared, evidenceReviewed })
    pending.current = false; setResult(outcome); setStage('done'); onResult(outcome, raw.id)
  }
  if (!open) return <button type="button" className={styles.linkBtn} disabled={facts.busy} onClick={() => setOpen(true)}>Correct saved {spec.label.toLowerCase()}</button>
  return <div className={styles.factEditor} data-fact-editor={field}>
    <strong>{spec.label}</strong>
    <p>Record {raw.id} · {raw.sr_winning_school || raw.current_champion || 'School not recorded'} · {raw.sr_sport || 'Sport not recorded'} · {raw.sr_season_year || 'Season not recorded'}</p>
    <p>Stored now: {renderValue(field, raw[field])}</p>
    <p>A manual correction changes this championship only, not the contact directory. A typed coach name never supplies an email or verified contact identity.</p>
    <fieldset disabled={facts.busy || stage === 'working' || stage === 'done'}>
      <label>New {spec.label.toLowerCase()}
        {spec.kind === 'textarea' ? <textarea className="input" rows={4} value={value} onChange={event => { setValue(event.target.value); resetReview() }} />
          : <input className="input" type={spec.kind === 'url' ? 'url' : 'text'} value={value} onChange={event => { setValue(event.target.value); resetReview() }} />}
      </label>
      {!value.trim() && <label><input type="checkbox" checked={declared} onChange={event => { setDeclared(event.target.checked); setStage('edit') }} /> Deliberately clear this field.</label>}
      <label>Reason for this manual correction (required)
        <textarea className="input" rows={3} value={reason} onChange={event => { setReason(event.target.value); setStage('edit'); setResult(null) }} />
      </label>
      {['champion_story', 'champion_story_source'].includes(field) && evidence && <label>
        <input type="checkbox" checked={evidenceReviewed} onChange={event => { setEvidenceReviewed(event.target.checked); setStage('edit') }} />
        I checked that {evidence} supports this exact story. If not, correct or clear the story evidence URL first.
      </label>}
      {problem && stage === 'edit' && <p role="status">{problem}</p>}
      {stage === 'edit' && <button type="button" className="btn btn-primary" disabled={!!problem} onClick={() => setStage('review')}>Review correction</button>}
      {stage === 'review' && <div><p>Replace {renderValue(field, raw[field])} with {renderValue(field, value)} on record {raw.id}?</p>
        <p>Reason: {reason}. The prior value and reason go through the existing audit path. Saving is verified by a direct readback; audit-log delivery is not independently confirmed here.</p>
        <button type="button" className="btn btn-primary" disabled={!!problem} onClick={save}>Save and verify</button>
        <button type="button" className={styles.reviseBtn} onClick={() => setStage('edit')}>Back</button>
      </div>}
      <button type="button" className={styles.reviseBtn} onClick={() => { setOpen(false); setStage('edit') }}>Cancel</button>
    </fieldset>
    {stage === 'working' && <p role="status">Saving, then reading the exact record back…</p>}
    {result && <p role="status">{result.message}</p>}
    {stage === 'done' && result?.state !== 'saved' && <button type="button" className="btn" onClick={facts.reload}>Reload record before another edit</button>}
  </div>
}
